import React, { useEffect, useMemo, useState } from 'react';
import AppShell from '@/components/AppShell';
import CompanyGate from '@/components/CompanyGate';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/components/ui/date-picker';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { ArrowLeft, FileText, FileSpreadsheet, Image as ImageIcon, Package, Gem, Coins, Hammer, RefreshCw, ChevronRight, ChevronDown, Circle, Sparkles, CalendarDays, LayoutList, LayoutGrid, Component } from 'lucide-react';
import { formatINR, formatCarats, formatGrams } from '@/lib/format';
import { PdfDownloadDialog } from '@/components/phase11/PdfDownloadDialog';
import { ImageListPdfDialog } from '@/components/reports/ImageListPdfDialog';
import { ImageThumb } from '@/components/ImageThumb';
import { toast } from 'sonner';
import { extractError } from '@/lib/errors';

export default function UnifiedClosingBalance() {
  const { effectiveCompanyId, canViewValues } = useAuth();
  const navigate = useNavigate();
  return (
    <AppShell
      title="Unified Closing Stock"
      subtitle="Stock In Hand · On Approval · Work In Progress — as on a chosen date"
      right={
        <Button variant="outline" onClick={() => navigate('/reports')} className="h-9" data-testid="unified-back-button">
          <ArrowLeft size={14} className="mr-1" /> Back to Reports
        </Button>
      }
    >
      <CompanyGate>
        <UnifiedContent companyId={effectiveCompanyId} />
      </CompanyGate>
    </AppShell>
  );
}

function UnifiedContent({ companyId }) {
  // Wave 3.41 — Restricted users hide Rate / Value columns and the
  // monetary summary tiles. Destructured here directly so the
  // sub-component is self-contained.
  // Wave 3.99ab — Also read ``hasMaterialAccess`` so material-scoped
  // buyers (Diamond Buyer / CS+Pearl Buyer / Gold Buyer / Mountings
  // manager) only see their own material sections + subsections and
  // the cross-material WIP / Customer Repair Items sections stay
  // hidden entirely.
  const { canViewValues, hasMaterialAccess, user } = useAuth();
  const isAdmin = user?.role === 'super_admin' || user?.role === 'company_admin';
  const canSeeGold = isAdmin || hasMaterialAccess('gold', 'see');
  const canSeeDiamonds = isAdmin || hasMaterialAccess('diamond', 'see');
  const canSeeColorStones = isAdmin || hasMaterialAccess('color_stone', 'see');
  const canSeePearls = isAdmin || hasMaterialAccess('pearl', 'see');
  const canSeeMountings = isAdmin || hasMaterialAccess('mountings', 'see');
  // Any material missing → the user is "scoped" (buyer-style). This
  // controls whether we render the cross-material WIP + Customer
  // Repair sections and whether the Grand Total tile shows.
  const isFullScope = canSeeGold && canSeeDiamonds && canSeeColorStones
    && canSeePearls && canSeeMountings;
  const [searchParams, setSearchParams] = useSearchParams();
  // Default date = today (YYYY-MM-DD)
  const todayIso = useMemo(() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }, []);
  const urlDate = searchParams.get('date') || '';
  const urlView = (searchParams.get('view') || '').toLowerCase() === 'expanded' ? 'expanded' : 'collapsed';
  const [asOfDate, setAsOfDate] = useState(urlDate || todayIso);
  const [view, setView] = useState(urlView); // Wave 3.38 — Collapsed (default) / Expanded
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [excelOpen, setExcelOpen] = useState(false);
  // Wave 3.55 — Image List PDF dialog
  const [imgListOpen, setImgListOpen] = useState(false);

  const formatAsOfLabel = (iso) => {
    if (!iso) return '';
    try {
      const [y, , d] = iso.split('-');
      const month = new Date(`${iso}T00:00:00`).toLocaleString('en-GB', { month: 'short' });
      return `${d} ${month} ${y}`;
    } catch {
      return iso;
    }
  };

  const load = async (effectiveDate) => {
    if (!companyId) return;
    setLoading(true);
    try {
      const params = { company_id: companyId };
      if (effectiveDate) params.as_of_date = effectiveDate;
      const res = await api.get('/reports/closing-balance/unified', { params });
      setData(res.data);
    } catch (e) {
      toast.error(extractError(e, 'Failed to load unified closing stock'));
    } finally {
      setLoading(false);
    }
  };
  // Reload whenever company or the as-of date changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(asOfDate); }, [companyId, asOfDate]);

  // Apply a new as-of date both to local state AND to the URL so the
  // page can be deep-linked / shared. Done synchronously in the handler
  // (no effect) to keep React's render contract clean.
  const applyAsOfDate = (nextDate) => {
    const v = nextDate || todayIso;
    setAsOfDate(v);
    if (searchParams.get('date') !== v) {
      const next = new URLSearchParams(searchParams);
      next.set('date', v);
      setSearchParams(next, { replace: true });
    }
  };

  // Wave 3.38 — persist the view selection in the URL too so a screenshot
  // / deep-link reflects the user's choice. Defaults to "collapsed".
  const applyView = (nextView) => {
    const v = nextView === 'expanded' ? 'expanded' : 'collapsed';
    setView(v);
    const next = new URLSearchParams(searchParams);
    if (v === 'collapsed') {
      // Default — omit the query key for cleaner URLs.
      next.delete('view');
    } else {
      next.set('view', v);
    }
    setSearchParams(next, { replace: true });
  };

  if (loading || !data) {
    return <div className="text-slate-500 py-8" data-testid="unified-loading">Loading…</div>;
  }

  const sih = data.stock_in_hand || {};
  const oa = data.on_approval || {};
  const wip = data.work_in_progress || {};
  // Wave 3.59 — Broken In-Hand + Broken Issued Out sections.
  const bih = data.broken_in_hand || {};
  const bio = data.broken_issued_out || {};
  const bihTotalValue = (bih.diamonds?.totals?.value || 0)
    + (bih.color_stones?.totals?.value || 0)
    + (bih.pearls?.totals?.value || 0);
  const bioTotalValue = (bio.diamonds?.totals?.value || 0)
    + (bio.color_stones?.totals?.value || 0)
    + (bio.pearls?.totals?.value || 0);
  const sihTotalValue = (sih.diamonds?.totals?.value || 0)
    + (sih.gold?.totals?.value || 0)
    + (sih.color_stones?.totals?.value || 0)
    + (sih.pearls?.totals?.value || 0)
    // Wave 3.99aa — Include Mountings in the Stock In Hand total.
    + (sih.mountings?.totals?.value || 0);
  const oaTotalValue = (oa.diamonds?.totals?.value || 0)
    + (oa.color_stones?.totals?.value || 0)
    + (oa.pearls?.totals?.value || 0);
  const wipTotalValue = (wip.totals?.diamond_value || 0)
    + (wip.totals?.gold_value || 0)
    + (wip.totals?.color_stone_value || 0)
    + (wip.totals?.pearl_value || 0);
  // Wave 3.59 — Grand Total now includes broken in-hand + broken issued out.
  const grandTotalValue = sihTotalValue + oaTotalValue + bihTotalValue + bioTotalValue + wipTotalValue;

  return (
    <div className="space-y-5">
      {/* As-on date selector + actions */}
      <Card className="p-4 flex flex-wrap items-end justify-between gap-3" data-testid="unified-toolbar">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label htmlFor="unified-page-as-of-date" className="text-[11px] text-slate-600 flex items-center gap-1 mb-1">
              <CalendarDays size={12} /> Report as on
            </Label>
            <DatePicker
              value={asOfDate}
              maxDate={todayIso}
              onChange={(e) => applyAsOfDate(e.target.value)}
              className="h-9 w-44"
              data-testid="unified-page-as-of-date-input"
            />
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-9 text-xs text-slate-500 hover:text-emerald-700"
            onClick={() => applyAsOfDate(todayIso)}
            data-testid="unified-as-of-today-button"
          >
            Reset to today
          </Button>
          <div className="text-[11px] text-slate-500 self-center ml-2" data-testid="unified-as-of-label">
            Showing snapshot <span className="font-medium text-emerald-700">{formatAsOfLabel(asOfDate)}</span>
            <span className="text-slate-400"> · generated {(data.generated_at || '').slice(0, 19).replace('T', ' ')} UTC</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex items-center gap-2 mr-1" data-testid="unified-view-toggle-group">
            <Label className="text-[11px] text-slate-600">View</Label>
            <ToggleGroup
              type="single"
              value={view}
              onValueChange={(v) => v && applyView(v)}
              className="bg-slate-100 rounded-md p-0.5"
            >
              <ToggleGroupItem
                value="collapsed"
                aria-label="Collapsed view"
                className="h-8 px-3 text-xs data-[state=on]:bg-white data-[state=on]:text-emerald-700 data-[state=on]:shadow-sm"
                data-testid="unified-view-toggle-collapsed"
              >
                <LayoutList size={12} className="mr-1.5" /> Collapsed
              </ToggleGroupItem>
              <ToggleGroupItem
                value="expanded"
                aria-label="Expanded view"
                className="h-8 px-3 text-xs data-[state=on]:bg-white data-[state=on]:text-emerald-700 data-[state=on]:shadow-sm"
                data-testid="unified-view-toggle-expanded"
              >
                <LayoutGrid size={12} className="mr-1.5" /> Expanded
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
          <Button variant="outline" size="sm" onClick={() => load(asOfDate)} data-testid="unified-refresh-button">
            <RefreshCw size={12} className="mr-1.5" /> Refresh
          </Button>
          <Button className="luxury-button" size="sm" onClick={() => setPdfOpen(true)} data-testid="unified-export-pdf">
            <FileText size={14} className="mr-1.5" /> Export PDF
          </Button>
          <Button variant="outline" size="sm" onClick={() => setExcelOpen(true)} data-testid="unified-export-excel">
            <FileSpreadsheet size={14} className="mr-1.5" /> Export Excel
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setImgListOpen(true)}
            data-testid="unified-image-list-pdf-button"
            title="Catalogue-style PDF with packet images"
          >
            <ImageIcon size={14} className="mr-1.5" /> Image List PDF
          </Button>
        </div>
      </Card>

      {/* Summary stats. Wave 3.41 — entire row hidden for users
          without ``values.view`` (these tiles are all monetary).
          Wave 3.99ab — Scoped buyers see only Stock In Hand and On
          Approval tiles (WIP + Grand Total across sections would
          leak cross-material context). */}
      {canViewValues && (
        <div className={`grid grid-cols-1 gap-3 ${isFullScope ? 'md:grid-cols-4' : 'md:grid-cols-2'}`}>
          <SummaryStat
            tone="emerald"
            icon={<Package size={16} />}
            label="Stock In Hand"
            value={formatINR(sihTotalValue)}
            sub={`${(sih.diamonds?.totals?.packets || 0)} packets · ${formatGrams(sih.gold?.totals?.pure_grams_999 || 0)} g`}
            testid="stat-stock-in-hand"
          />
          <SummaryStat
            tone="amber"
            icon={<Package size={16} />}
            label="On Approval"
            value={formatINR(oaTotalValue)}
            sub={`${(oa.diamonds?.totals?.packets || 0)} packets`}
            testid="stat-on-approval"
          />
          {isFullScope && (
            <SummaryStat
              tone="violet"
              icon={<Hammer size={16} />}
              label="Work In Progress"
              value={formatINR(wipTotalValue)}
              sub={`${(wip.goldsmiths || []).length} goldsmiths · ${(wip.totals?.diamond_pieces || 0) + (wip.totals?.color_stone_pieces || 0) + (wip.totals?.pearl_pieces || 0)} pcs`}
              testid="stat-wip"
            />
          )}
          {isFullScope && (
            <SummaryStat
              tone="navy"
              icon={<Package size={16} />}
              label="Grand Total"
              value={formatINR(grandTotalValue)}
              sub="Across all sections"
              testid="stat-grand-total"
            />
          )}
        </div>
      )}

      {/* Section 1: Stock In Hand — Gold first, then Diamonds, then Color Stones / Pearls hooks (Phase 13.2) */}
      <Card className="overflow-hidden" data-testid="section-stock-in-hand">
        <div className="px-5 py-3 bg-emerald-700 text-white flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package size={16} />
            <span className="font-medium">1. Stock In Hand</span>
          </div>
          {canViewValues && (
            <Badge className="bg-white/20 text-white border-white/30">{formatINR(sihTotalValue)}</Badge>
          )}
        </div>
        <div className="p-5 space-y-6">
          {/* Gold — first. Wave 3.99ab — hidden for material-scoped
              users without gold visibility. */}
          {canSeeGold && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Coins size={14} className="text-[var(--gold-icon)]" />
              <span className="font-medium text-[var(--ink-navy)]">Gold</span>
              <Badge variant="secondary" className="text-[10px]">{(sih.gold?.by_purity || []).length} purities</Badge>
            </div>
            {(sih.gold?.by_purity || []).length === 0 ? (
              <div className="text-sm text-slate-500 italic py-3">No gold balance.</div>
            ) : (
              <table className="w-full text-sm" data-testid="sih-gold-table">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-3 py-2">Purity</th>
                    <th className="text-right px-3 py-2">999 Grams</th>
                    {canViewValues && <th className="text-right px-3 py-2">Rate (₹/g)</th>}
                    {canViewValues && <th className="text-right px-3 py-2">Value</th>}
                  </tr>
                </thead>
                <tbody>
                  {sih.gold.by_purity.map((p) => (
                    <tr key={p.purity_id} className="border-t border-[var(--divider)]">
                      <td className="px-3 py-2">
                        {p.purity_name} <span className="text-slate-500 text-xs">({p.purity_percent.toFixed(2)}%)</span>
                      </td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums">{formatGrams(p.pure_grams_999)}</td>
                      {canViewValues && <td className="px-3 py-2 text-right font-mono tabular-nums">{p.rate_per_gram_999.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>}
                      {canViewValues && <td className="px-3 py-2 text-right font-mono tabular-nums">{formatINR(p.value)}</td>}
                    </tr>
                  ))}
                  <tr className="border-t-2 border-emerald-300 bg-emerald-50/60 font-medium">
                    <td className="px-3 py-2">TOTAL</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">{formatGrams(sih.gold.totals.pure_grams_999 || 0)}</td>
                    {canViewValues && <td></td>}
                    {canViewValues && <td className="px-3 py-2 text-right font-mono tabular-nums">{formatINR(sih.gold.totals.value || 0)}</td>}
                  </tr>
                </tbody>
              </table>
            )}
          </div>
          )}

          {/* Wave 3.99aa — Mountings (Semi-Finished Gold Pieces).
              Placed right after Gold per operator spec. Values are live
              gold-marked: grams × mounting purity × today's 999 rate.
              Wave 3.99ab — Scope-gated. */}
          {canSeeMountings && (
          <div data-testid="sih-mountings-section">
            <div className="flex items-center gap-2 mb-2">
              <Component size={14} className="text-[var(--accent-warm,#B45309)]" />
              <span className="font-medium text-[var(--ink-navy)]">Mountings</span>
              <Badge variant="secondary" className="text-[10px]">
                {(sih.mountings?.items || []).length} items
              </Badge>
              {(sih.mountings?.gold_rate_per_gram_999 || 0) > 0 && canViewValues && (
                <span className="text-[10.5px] text-slate-500 ml-1">
                  · rate {formatINR(sih.mountings.gold_rate_per_gram_999)} / g
                </span>
              )}
            </div>
            {((sih.mountings?.items || []).length === 0) ? (
              <div className="text-sm text-slate-500 italic py-3">No mountings in stock.</div>
            ) : (
              <table className="w-full text-sm" data-testid="sih-mountings-table">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-3 py-2">Mounting</th>
                    <th className="text-left px-3 py-2">Purity</th>
                    <th className="text-right px-3 py-2">Stock (g)</th>
                    {canViewValues && <th className="text-right px-3 py-2">Rate (₹/g)</th>}
                    {canViewValues && <th className="text-right px-3 py-2">Value</th>}
                  </tr>
                </thead>
                <tbody>
                  {(sih.mountings?.items || []).map((it) => (
                    <tr key={it.mounting_id} className="border-t border-[var(--divider)]" data-testid={`sih-mountings-row-${it.mounting_id}`}>
                      <td className="px-3 py-2 font-medium text-slate-800">{it.name}</td>
                      <td className="px-3 py-2">
                        {it.purity_name}{' '}
                        <span className="text-slate-500 text-xs">({(it.purity_percent || 0).toFixed(2)}%)</span>
                      </td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums">{formatGrams(it.stock_grams)}</td>
                      {canViewValues && (
                        <td className="px-3 py-2 text-right font-mono tabular-nums">
                          {(it.rate_per_gram_999 || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </td>
                      )}
                      {canViewValues && (
                        <td className="px-3 py-2 text-right font-mono tabular-nums">
                          {formatINR(it.stock_value || 0)}
                        </td>
                      )}
                    </tr>
                  ))}
                  <tr className="border-t-2 border-emerald-300 bg-emerald-50/60 font-medium">
                    <td className="px-3 py-2">TOTAL</td>
                    <td></td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">
                      {formatGrams(sih.mountings?.totals?.stock_grams || 0)}
                    </td>
                    {canViewValues && <td></td>}
                    {canViewValues && (
                      <td className="px-3 py-2 text-right font-mono tabular-nums">
                        {formatINR(sih.mountings?.totals?.value || 0)}
                      </td>
                    )}
                  </tr>
                </tbody>
              </table>
            )}
          </div>
          )}

          {/* Diamonds — expandable per classification (Phase 13.3).
              Wave 3.99ab — Scope-gated. */}
          {canSeeDiamonds && (
          <CaratStockInHand
            label="Diamonds"
            icon={<Gem size={14} className="text-[var(--diamond-icon)]" />}
            section={sih.diamonds || {}}
            badgeClassname="bg-[var(--diamond-bg)] text-[var(--diamond-icon)]"
            packetLinkPrefix="/diamonds/packets"
            testidPrefix="diamonds"
            emptyMsg="No purchased diamond stock."
            view={view}
            canViewValues={canViewValues}
          />
          )}

          {/* Phase 16 — Color Stones (live data, mirrors Diamonds).
              Wave 3.99ab — Scope-gated. */}
          {canSeeColorStones && (
          <CaratStockInHand
            label="Color Stones"
            icon={<Sparkles size={14} className="text-pink-500" />}
            section={sih.color_stones || {}}
            badgeClassname="bg-pink-50 text-pink-700"
            packetLinkPrefix="/color-stones/packets"
            testidPrefix="color-stones"
            emptyMsg="No purchased colour-stone stock."
            extraColumn={{ label: 'Stone', field: 'stone_type' }}
            view={view}
            canViewValues={canViewValues}
          />
          )}

          {/* Phase 16 — Pearls (live data, mirrors Diamonds).
              Wave 3.99ab — Scope-gated. */}
          {canSeePearls && (
          <CaratStockInHand
            label="Pearls"
            icon={<Circle size={14} className="text-slate-500" />}
            section={sih.pearls || {}}
            badgeClassname="bg-slate-100 text-slate-700"
            packetLinkPrefix="/pearls/packets"
            testidPrefix="pearls"
            emptyMsg="No purchased pearl stock."
            extraColumn={{ label: 'Type', field: 'piece_type' }}
            view={view}
            canViewValues={canViewValues}
          />
          )}
        </div>
      </Card>

      {/* Section 2: On Approval */}
      <Card className="overflow-hidden" data-testid="section-on-approval">
        <div className="px-5 py-3 bg-amber-600 text-white flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package size={16} />
            <span className="font-medium">2. On Approval Stock</span>
          </div>
          {canViewValues && (
            <Badge className="bg-white/20 text-white border-white/30">{formatINR(oaTotalValue)}</Badge>
          )}
        </div>
        <div className="p-5 space-y-6">
          {/* Wave 3.99ab — Scope-gated per stone material. */}
          {canSeeDiamonds && (
          <OnApprovalSection
            label="Diamonds"
            icon={<Gem size={14} className="text-[var(--diamond-icon)]" />}
            section={oa.diamonds || {}}
            emptyMsg="No diamond packets on approval."
            testidPrefix="diamonds"
            view={view}
            packetLinkPrefix="/diamonds/packets"
            canViewValues={canViewValues}
          />
          )}
          {canSeeColorStones && (
          <OnApprovalSection
            label="Color Stones"
            icon={<Sparkles size={14} className="text-pink-500" />}
            section={oa.color_stones || {}}
            emptyMsg="No colour-stone packets on approval."
            testidPrefix="color-stones"
            view={view}
            packetLinkPrefix="/color-stones/packets"
            canViewValues={canViewValues}
          />
          )}
          {canSeePearls && (
          <OnApprovalSection
            label="Pearls"
            icon={<Circle size={14} className="text-slate-500" />}
            section={oa.pearls || {}}
            emptyMsg="No pearl packets on approval."
            testidPrefix="pearls"
            view={view}
            packetLinkPrefix="/pearls/packets"
            canViewValues={canViewValues}
          />
          )}
        </div>
      </Card>

      {/* Wave 3.59 — Section 2b: Broken In-Hand.
          Wave 3.99ab — Whole card hidden if the user cannot see any
          of Diamonds / CS / Pearls (Gold Buyer & Mountings scope). */}
      {(canSeeDiamonds || canSeeColorStones || canSeePearls) && (
      <Card className="overflow-hidden" data-testid="section-broken-in-hand">
        <div className="px-5 py-3 bg-orange-700 text-white flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package size={16} />
            <span className="font-medium">3. Broken In-Hand</span>
            <span className="text-[11px] opacity-80 hidden sm:inline">
              &middot; Salvage packets still with the company
            </span>
          </div>
          {canViewValues && (
            <Badge className="bg-white/20 text-white border-white/30">{formatINR(bihTotalValue)}</Badge>
          )}
        </div>
        <div className="p-5 space-y-6">
          {canSeeDiamonds && (
          <CaratStockInHand
            label="Diamonds"
            icon={<Gem size={14} className="text-[var(--diamond-icon)]" />}
            section={bih.diamonds || {}}
            emptyMsg="No broken diamond packets in-house."
            testidPrefix="broken-diamonds"
            view={view}
            packetLinkPrefix="/diamonds/packets"
            canViewValues={canViewValues}
          />
          )}
          {canSeeColorStones && (
          <CaratStockInHand
            label="Color Stones"
            icon={<Sparkles size={14} className="text-pink-500" />}
            section={bih.color_stones || {}}
            emptyMsg="No broken colour-stone packets in-house."
            testidPrefix="broken-color-stones"
            view={view}
            packetLinkPrefix="/color-stones/packets"
            canViewValues={canViewValues}
          />
          )}
          {canSeePearls && (
          <CaratStockInHand
            label="Pearls"
            icon={<Circle size={14} className="text-slate-500" />}
            section={bih.pearls || {}}
            emptyMsg="No broken pearl packets in-house."
            testidPrefix="broken-pearls"
            view={view}
            packetLinkPrefix="/pearls/packets"
            canViewValues={canViewValues}
          />
          )}
        </div>
      </Card>
      )}

      {/* Wave 3.59 — Section 2c: Broken Issued Out.
          Wave 3.99ab — Hidden if user has no stone material scope. */}
      {(canSeeDiamonds || canSeeColorStones || canSeePearls) && (
      <Card className="overflow-hidden" data-testid="section-broken-issued-out">
        <div className="px-5 py-3 bg-orange-500 text-white flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package size={16} />
            <span className="font-medium">4. Broken Issued Out</span>
            <span className="text-[11px] opacity-80 hidden sm:inline">
              &middot; With Vendor / Cutter for recutting
            </span>
          </div>
          {canViewValues && (
            <Badge className="bg-white/20 text-white border-white/30">{formatINR(bioTotalValue)}</Badge>
          )}
        </div>
        <div className="p-5 space-y-6">
          {canSeeDiamonds && (
          <BrokenIssuedOutSection
            label="Diamonds"
            icon={<Gem size={14} className="text-[var(--diamond-icon)]" />}
            section={bio.diamonds || {}}
            emptyMsg="No diamond broken packets outstanding with a vendor / cutter."
            testidPrefix="bio-diamonds"
            view={view}
            canViewValues={canViewValues}
          />
          )}
          {canSeeColorStones && (
          <BrokenIssuedOutSection
            label="Color Stones"
            icon={<Sparkles size={14} className="text-pink-500" />}
            section={bio.color_stones || {}}
            emptyMsg="No colour-stone broken packets outstanding with a vendor / cutter."
            testidPrefix="bio-color-stones"
            view={view}
            canViewValues={canViewValues}
          />
          )}
          {canSeePearls && (
          <BrokenIssuedOutSection
            label="Pearls"
            icon={<Circle size={14} className="text-slate-500" />}
            section={bio.pearls || {}}
            emptyMsg="No pearl broken packets outstanding with a vendor / cutter."
            testidPrefix="bio-pearls"
            view={view}
            canViewValues={canViewValues}
          />
          )}
        </div>
      </Card>
      )}

      {/* Section 5: Work In Progress.
          Wave 3.99ab — Hidden for material-scoped users because WIP
          rows mix all four core materials per goldsmith/order. */}
      {isFullScope && (
      <Card className="overflow-hidden" data-testid="section-wip">
        <div className="px-5 py-3 bg-violet-700 text-white flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Hammer size={16} />
            <span className="font-medium">5. Work In Progress (with Goldsmiths)</span>
          </div>
          <Badge className="bg-white/20 text-white border-white/30">{formatINR(wipTotalValue)}</Badge>
        </div>
        <div className="p-5 space-y-4">
          {(wip.goldsmiths || []).length === 0 ? (
            <div className="text-sm text-slate-500 italic py-3">No raw material currently with goldsmiths for active or on-hold orders.</div>
          ) : (
            wip.goldsmiths.map((g) => (
              <div key={g.goldsmith_id} className="border border-[var(--divider)] rounded-md overflow-hidden" data-testid={`wip-goldsmith-${g.goldsmith_id}`}>
                <div className="px-3 py-2 bg-violet-50 flex items-center justify-between gap-3">
                  <div className="text-sm font-medium text-violet-900">{g.goldsmith_name}</div>
                  <div className="text-[11px] font-mono text-violet-700 flex flex-wrap justify-end gap-x-3 gap-y-0.5">
                    <span>Dia: {formatCarats(g.subtotal.diamond_carats)} ct · {g.subtotal.diamond_pieces} pcs</span>
                    <span>Gold: {formatGrams(g.subtotal.gold_grams_999)} g999</span>
                    {(g.subtotal.color_stone_carats > 0 || g.subtotal.color_stone_pieces > 0) && (
                      <span>CS: {formatCarats(g.subtotal.color_stone_carats)} ct · {g.subtotal.color_stone_pieces} pcs</span>
                    )}
                    {(g.subtotal.pearl_carats > 0 || g.subtotal.pearl_pieces > 0) && (
                      <span>Pearl: {formatCarats(g.subtotal.pearl_carats)} ct · {g.subtotal.pearl_pieces} pcs</span>
                    )}
                  </div>
                </div>
                <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[820px]">
                  <thead className="bg-slate-50/60">
                    <tr>
                      <th className="text-left px-3 py-2">Order</th>
                      <th className="text-left px-3 py-2">Stage</th>
                      <th className="text-right px-3 py-2">Diamonds<br/><span className="text-[10px] font-normal text-slate-500">ct / pcs</span></th>
                      <th className="text-right px-3 py-2">Gold<br/><span className="text-[10px] font-normal text-slate-500">g 999</span></th>
                      <th className="text-right px-3 py-2">Color Stones<br/><span className="text-[10px] font-normal text-slate-500">ct / pcs</span></th>
                      <th className="text-right px-3 py-2">Pearls<br/><span className="text-[10px] font-normal text-slate-500">ct / pcs</span></th>
                      <th className="text-right px-3 py-2">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.orders.map((o) => (
                      <tr key={o.order_id} className="border-t border-[var(--divider)]" data-testid={`wip-order-${o.order_id}`}>
                        <td className="px-3 py-2">
                          <div className="font-mono text-[11px] text-[var(--ink-navy)]">{o.order_no}</div>
                          <div className="text-[11px] text-slate-500 truncate max-w-xs" title={o.title}>{o.title}</div>
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {o.stage || '—'}
                          {o.hold_status === 'on_hold' && <Badge className="ml-1 bg-amber-50 text-amber-700 text-[9px]">On Hold</Badge>}
                        </td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums text-xs">
                          {formatCarats(o.diamond_carats || 0)} / {o.diamond_pieces || 0}
                        </td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums text-xs">
                          {formatGrams(o.gold_grams_999 || 0)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums text-xs">
                          {formatCarats(o.color_stone_carats || 0)} / {o.color_stone_pieces || 0}
                        </td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums text-xs">
                          {formatCarats(o.pearl_carats || 0)} / {o.pearl_pieces || 0}
                        </td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums">
                          {formatINR((o.diamond_value || 0) + (o.gold_value || 0) + (o.color_stone_value || 0) + (o.pearl_value || 0))}
                        </td>
                      </tr>
                    ))}
                    {g.unattributed && (
                      <tr className="border-t border-[var(--divider)] bg-slate-50/50">
                        <td className="px-3 py-2 text-slate-600 italic">Unattributed</td>
                        <td className="px-3 py-2 text-xs">—</td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums text-xs">
                          {formatCarats(g.unattributed.diamond_carats || 0)} / {g.unattributed.diamond_pieces || 0}
                        </td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums text-xs">
                          {formatGrams(g.unattributed.gold_grams_999 || 0)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums text-xs">
                          {formatCarats(g.unattributed.color_stone_carats || 0)} / {g.unattributed.color_stone_pieces || 0}
                        </td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums text-xs">
                          {formatCarats(g.unattributed.pearl_carats || 0)} / {g.unattributed.pearl_pieces || 0}
                        </td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums">
                          {formatINR((g.unattributed.diamond_value || 0) + (g.unattributed.gold_value || 0) + (g.unattributed.color_stone_value || 0) + (g.unattributed.pearl_value || 0))}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                </div>
              </div>
            ))
          )}
        </div>
      </Card>
      )}

      {/* Wave 3.45 — Customer Repair Items in WIP (Phase 1).
          FULLY SEPARATE from the regular WIP section above. External
          (customer) items are CUSTOMER property — weights only, no ₹
          value. Internal repair items are own stock and could be
          factored into valuation later; for Phase 1 we show them with
          weights only too.
          Wave 3.99ab — Hidden for material-scoped users. */}
      {isFullScope && <CustomerRepairItemsWipSection wip={wip} />}

      <PdfDownloadDialog
        open={pdfOpen}
        onOpenChange={setPdfOpen}
        title={`Unified Closing Stock as on ${formatAsOfLabel(asOfDate)} — PDF`}
        description={`Choose whether to include the company logo and name on this report. Current view: ${view === 'expanded' ? 'Expanded' : 'Collapsed'}.`}
        companyId={companyId}
        endpoint="/reports/closing-balance/unified/pdf"
        filename={`unified_closing_stock_${asOfDate || todayIso}_${view}.pdf`}
        extraParams={{ company_id: companyId, as_of_date: asOfDate || todayIso, view }}
        mode="download"
      />
      <PdfDownloadDialog
        open={excelOpen}
        onOpenChange={setExcelOpen}
        title={`Unified Closing Stock as on ${formatAsOfLabel(asOfDate)} — Excel`}
        description={`Choose whether to include the company logo and name on this report. Current view: ${view === 'expanded' ? 'Expanded' : 'Collapsed'}. (Images are not embedded in Excel.)`}
        companyId={companyId}
        endpoint="/reports/closing-balance/unified/excel"
        filename={`unified_closing_stock_${asOfDate || todayIso}_${view}.xlsx`}
        extraParams={{ company_id: companyId, as_of_date: asOfDate || todayIso, view }}
        mode="download"
      />
      <ImageListPdfDialog
        open={imgListOpen}
        onOpenChange={setImgListOpen}
        companyId={companyId}
        scope="unified"
        moduleLabel="Unified Closing Stock"
        defaultFilename={`image_list_unified_${asOfDate || todayIso}.pdf`}
      />
    </div>
  );
}

function SummaryStat({ tone, icon, label, value, sub, testid }) {
  const toneMap = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    violet: 'bg-violet-50 border-violet-200 text-violet-900',
    navy: 'bg-[var(--paper-warm)] border-[var(--divider)] text-[var(--ink-navy)]',
  };
  const iconTone = {
    emerald: 'text-emerald-700',
    amber: 'text-amber-700',
    violet: 'text-violet-700',
    navy: 'text-[var(--ink-navy)]',
  };
  return (
    <div className={`p-4 rounded-lg border ${toneMap[tone] || toneMap.navy}`} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide opacity-80">
        <span className={iconTone[tone] || iconTone.navy}>{icon}</span>
        <span>{label}</span>
      </div>
      <div className="text-xl font-medium mt-1 font-mono tabular-nums">{value}</div>
      <div className="text-[11px] opacity-70 mt-0.5">{sub}</div>
    </div>
  );
}

// Phase 16 — Generic carat-based stock-in-hand section.
// Used for Diamonds, Color Stones, and Pearls (all share the same shape).
// Wave 3.38 — Adds an `Image` column inside the per-packet drilldown and
// auto-expands every classification when the top-level view is "expanded".
function CaratStockInHand({ label, icon, section, badgeClassname, packetLinkPrefix, testidPrefix, emptyMsg, extraColumn, view, canViewValues = true }) {
  const cls = section.classifications || [];
  const totals = section.totals || {};
  const isExpanded = view === 'expanded';
  // When the global view flips to "expanded", auto-open every classification.
  // When the user collapses it again, reset the per-row open state to {}.
  const initial = isExpanded ? Object.fromEntries(cls.map((c) => [c.classification_id, true])) : {};
  const [expanded, setExpanded] = useState(initial);
  const toggle = (id) => setExpanded((s) => ({ ...s, [id]: !s[id] }));
  // Re-seed when the view toggle changes.
  useEffect(() => {
    if (isExpanded) {
      setExpanded(Object.fromEntries(cls.map((c) => [c.classification_id, true])));
    } else {
      setExpanded({});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded, cls.length]);
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <span className="font-medium text-[var(--ink-navy)]">{label}</span>
        <Badge variant="secondary" className="text-[10px]">{cls.length} classifications</Badge>
        {cls.length > 0 && !isExpanded && (
          <span className="text-[11px] text-slate-500 ml-2">Click a row to see packets</span>
        )}
        {cls.length > 0 && isExpanded && (
          <span className="text-[11px] text-emerald-700 ml-2">Showing every packet</span>
        )}
      </div>
      {cls.length === 0 ? (
        <div className="text-sm text-slate-500 italic py-3">{emptyMsg}</div>
      ) : (
        <table className="w-full text-sm" data-testid={`sih-${testidPrefix}-table`}>
          <thead className="bg-slate-50">
            <tr>
              <th className="w-6"></th>
              <th className="text-left px-3 py-2">Classification</th>
              <th className="text-right px-3 py-2">Packets</th>
              <th className="text-right px-3 py-2">Carats</th>
              <th className="text-right px-3 py-2">Pieces</th>
              {canViewValues && <th className="text-right px-3 py-2">Value</th>}
            </tr>
          </thead>
          <tbody>
            {cls.map((c) => {
              const isOpen = !!expanded[c.classification_id];
              return (
                <React.Fragment key={c.classification_id}>
                  <tr
                    className="border-t border-[var(--divider)] cursor-pointer hover:bg-slate-50"
                    onClick={() => toggle(c.classification_id)}
                    data-testid={`sih-${testidPrefix}-row-${c.schema_code}`}
                  >
                    <td className="px-2 text-slate-500">
                      {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`font-mono text-xs ${badgeClassname} px-1.5 py-0.5 rounded`}>{c.schema_code}</span>
                      <span className="ml-2 text-slate-700">{c.shape_name}</span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">{c.packets.length}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">{formatCarats(c.subtotal.carats)}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">{c.subtotal.pieces}</td>
                    {canViewValues && <td className="px-3 py-2 text-right font-mono tabular-nums">{formatINR(c.subtotal.value)}</td>}
                  </tr>
                  {isOpen && c.packets.length > 0 && (
                    <tr className="bg-slate-50/40">
                      <td colSpan={canViewValues ? 6 : 5} className="px-3 py-2">
                        <div className="border border-[var(--divider)] rounded-md overflow-hidden bg-white">
                          <table className="w-full text-xs" data-testid={`sih-${testidPrefix}-packets-${c.schema_code}`}>
                            <thead className="bg-slate-100">
                              <tr>
                                <th className="w-12 px-3 py-1.5">Image</th>
                                <th className="text-left px-3 py-1.5">Packet</th>
                                <th className="text-left px-3 py-1.5">Valuation</th>
                                {extraColumn && <th className="text-left px-3 py-1.5">{extraColumn.label}</th>}
                                <th className="text-right px-3 py-1.5">Carats</th>
                                <th className="text-right px-3 py-1.5">Pieces</th>
                                {canViewValues && <th className="text-right px-3 py-1.5">Rate (₹/ct)</th>}
                                {canViewValues && <th className="text-right px-3 py-1.5">Value</th>}
                              </tr>
                            </thead>
                            <tbody>
                              {c.packets.map((p) => (
                                <tr
                                  key={p.id}
                                  className="border-t border-[var(--divider)]"
                                  data-testid={`sih-${testidPrefix}-packet-${p.packet_no}`}
                                >
                                  <td className="px-3 py-1.5">
                                    <ImageThumb
                                      src={p.image}
                                      alt={`${p.packet_no} image`}
                                      size={36}
                                      title={`${p.packet_no} preview`}
                                      testId={`sih-${testidPrefix}-thumb-${p.packet_no}`}
                                    />
                                  </td>
                                  <td className="px-3 py-1.5">
                                    <Link
                                      to={`${packetLinkPrefix}/${p.id}`}
                                      className="font-mono text-[var(--diamond-icon)] hover:underline"
                                    >
                                      {p.packet_no}
                                    </Link>
                                    {p.description && (
                                      <span className="ml-2 text-[10px] text-slate-500">{p.description}</span>
                                    )}
                                  </td>
                                  <td className="px-3 py-1.5">
                                    <Badge variant="secondary" className="text-[9px] uppercase tracking-wide">
                                      {p.valuation_mode === 'fixed' ? 'Fixed' : 'Running Avg'}
                                    </Badge>
                                  </td>
                                  {extraColumn && (
                                    <td className="px-3 py-1.5 text-[11px] text-slate-700">
                                      {p[extraColumn.field] || <span className="text-slate-400">—</span>}
                                      {extraColumn.field === 'piece_type' && p.piece_type === 'strand' && p.strand_count > 0 && (
                                        <span className="ml-1 text-[10px] text-slate-500">({p.strand_count} strand{p.strand_count > 1 ? 's' : ''})</span>
                                      )}
                                    </td>
                                  )}
                                  <td className="px-3 py-1.5 text-right font-mono tabular-nums">{formatCarats(p.carats)}</td>
                                  <td className="px-3 py-1.5 text-right font-mono tabular-nums">{p.pieces}</td>
                                  {canViewValues && (
                                    <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                                      {(p.rate_per_ct || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                                    </td>
                                  )}
                                  {canViewValues && <td className="px-3 py-1.5 text-right font-mono tabular-nums">{formatINR(p.value)}</td>}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            <tr className="border-t-2 border-emerald-300 bg-emerald-50/60 font-medium">
              <td></td>
              <td className="px-3 py-2">TOTAL</td>
              <td className="px-3 py-2 text-right font-mono tabular-nums">{totals.packets || 0}</td>
              <td className="px-3 py-2 text-right font-mono tabular-nums">{formatCarats(totals.carats || 0)}</td>
              <td className="px-3 py-2 text-right font-mono tabular-nums">{totals.pieces || 0}</td>
              {canViewValues && <td className="px-3 py-2 text-right font-mono tabular-nums">{formatINR(totals.value || 0)}</td>}
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
}


// Phase 16 — Generic on-approval section by vendor.
// Wave 3.38 — When `view === 'expanded'`, render a FLAT list of packets with
// thumbnails + Packet No + Classification + Vendor (one row per packet).
function OnApprovalSection({ label, icon, section, emptyMsg, testidPrefix, view, packetLinkPrefix, canViewValues = true }) {
  const by_vendor = section.by_vendor || [];
  const totals = section.totals || {};
  const isExpanded = view === 'expanded';

  // Flatten all packets across vendors for expanded mode.
  const flatPackets = [];
  if (isExpanded) {
    by_vendor.forEach((v) => {
      (v.packets || []).forEach((p) => {
        flatPackets.push({ ...p, _vendor_name: v.vendor_name || 'Unknown' });
      });
    });
  }

  return (
    <div data-testid={`oa-${testidPrefix}`}>
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <span className="font-medium text-[var(--ink-navy)]">{label}</span>
        <Badge variant="secondary" className="text-[10px]">{by_vendor.length} vendor(s)</Badge>
        {isExpanded && flatPackets.length > 0 && (
          <Badge variant="secondary" className="text-[10px] bg-emerald-50 text-emerald-700">{flatPackets.length} packets</Badge>
        )}
      </div>
      {by_vendor.length === 0 ? (
        <div className="text-sm text-slate-500 italic py-3">{emptyMsg}</div>
      ) : isExpanded ? (
        <table className="w-full text-sm" data-testid={`oa-${testidPrefix}-table-expanded`}>
          <thead className="bg-slate-50">
            <tr>
              <th className="w-12 px-3 py-2">Image</th>
              <th className="text-left px-3 py-2">Vendor</th>
              <th className="text-left px-3 py-2">Packet No</th>
              <th className="text-left px-3 py-2">Classification</th>
              {canViewValues && <th className="text-right px-3 py-2">Rate (₹/ct)</th>}
              <th className="text-right px-3 py-2">Carats</th>
              <th className="text-right px-3 py-2">Pieces</th>
              {canViewValues && <th className="text-right px-3 py-2">Value</th>}
            </tr>
          </thead>
          <tbody>
            {flatPackets.map((p) => {
              const clsLabel = (p.classification_schema || '') + (p.classification_shape ? ` — ${p.classification_shape}` : '');
              return (
                <tr
                  key={`${p._vendor_name}-${p.id || p.packet_no}`}
                  className="border-t border-[var(--divider)]"
                  data-testid={`oa-${testidPrefix}-packet-${p.packet_no}`}
                >
                  <td className="px-3 py-1.5">
                    <ImageThumb
                      src={p.image}
                      alt={`${p.packet_no} image`}
                      size={36}
                      title={`${p.packet_no} preview`}
                      testId={`oa-${testidPrefix}-thumb-${p.packet_no}`}
                    />
                  </td>
                  <td className="px-3 py-1.5 text-xs text-slate-700 max-w-[160px] truncate" title={p._vendor_name}>{p._vendor_name}</td>
                  <td className="px-3 py-1.5">
                    {packetLinkPrefix && p.id ? (
                      <Link to={`${packetLinkPrefix}/${p.id}`} className="font-mono text-xs text-[var(--diamond-icon)] hover:underline">
                        {p.packet_no}
                      </Link>
                    ) : (
                      <span className="font-mono text-xs">{p.packet_no}</span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-xs text-slate-700">
                    {clsLabel || <span className="text-slate-400">—</span>}
                  </td>
                  {canViewValues && (
                    <td className="px-3 py-1.5 text-right font-mono tabular-nums text-xs">
                      {(p.rate_per_ct || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                    </td>
                  )}
                  <td className="px-3 py-1.5 text-right font-mono tabular-nums">{formatCarats(p.carats)}</td>
                  <td className="px-3 py-1.5 text-right font-mono tabular-nums">{p.pieces}</td>
                  {canViewValues && <td className="px-3 py-1.5 text-right font-mono tabular-nums">{formatINR(p.value)}</td>}
                </tr>
              );
            })}
            <tr className="border-t-2 border-amber-300 bg-amber-50/60 font-medium">
              <td></td>
              <td className="px-3 py-2" colSpan={canViewValues ? 4 : 3}>TOTAL</td>
              <td className="px-3 py-2 text-right font-mono tabular-nums">{formatCarats(totals.carats || 0)}</td>
              <td className="px-3 py-2 text-right font-mono tabular-nums">{totals.pieces || 0}</td>
              {canViewValues && <td className="px-3 py-2 text-right font-mono tabular-nums">{formatINR(totals.value || 0)}</td>}
            </tr>
          </tbody>
        </table>
      ) : (
        <table className="w-full text-sm" data-testid={`oa-${testidPrefix}-table`}>
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left px-3 py-2">Vendor</th>
              <th className="text-right px-3 py-2">Packets</th>
              <th className="text-right px-3 py-2">Carats</th>
              <th className="text-right px-3 py-2">Pieces</th>
              {canViewValues && <th className="text-right px-3 py-2">Value</th>}
            </tr>
          </thead>
          <tbody>
            {by_vendor.map((v) => (
              <tr key={(v.vendor_id || 'unknown') + '-' + label} className="border-t border-[var(--divider)]">
                <td className="px-3 py-2">{v.vendor_name}</td>
                <td className="px-3 py-2 text-right font-mono tabular-nums">{v.packets.length}</td>
                <td className="px-3 py-2 text-right font-mono tabular-nums">{formatCarats(v.subtotal.carats)}</td>
                <td className="px-3 py-2 text-right font-mono tabular-nums">{v.subtotal.pieces}</td>
                {canViewValues && <td className="px-3 py-2 text-right font-mono tabular-nums">{formatINR(v.subtotal.value)}</td>}
              </tr>
            ))}
            <tr className="border-t-2 border-amber-300 bg-amber-50/60 font-medium">
              <td className="px-3 py-2">TOTAL</td>
              <td className="px-3 py-2 text-right font-mono tabular-nums">{totals.packets || 0}</td>
              <td className="px-3 py-2 text-right font-mono tabular-nums">{formatCarats(totals.carats || 0)}</td>
              <td className="px-3 py-2 text-right font-mono tabular-nums">{totals.pieces || 0}</td>
              {canViewValues && <td className="px-3 py-2 text-right font-mono tabular-nums">{formatINR(totals.value || 0)}</td>}
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
}


// Wave 3.45 — Phase 1 — Customer Repair Items in WIP
// =========================================================================
// Renders an additional WIP section sourced from the new
// ``work_in_progress.customer_repair_items`` payload. External items
// (customer-owned) are intentionally shown as WEIGHT-ONLY and excluded
// from the closing-stock ₹ value above. Internal items are listed but
// for Phase 1 they are also rendered weight-only — the underlying gold
// / diamond / CS / pearl quantities still flow through the normal

// Wave 3.59 — Broken Issued Out (per material). Mirrors OnApprovalSection
// look-and-feel but the primary grouping is Recipient (Vendor/Cutter)
// and rows drill down to individual issue memos.
function BrokenIssuedOutSection({ label, icon, section, emptyMsg, testidPrefix, view, canViewValues = true }) {
  const by_recipient = section.by_recipient || [];
  const totals = section.totals || {};
  const isExpanded = view === 'expanded';

  // Flat list of individual issues for expanded mode.
  const flatIssues = [];
  if (isExpanded) {
    by_recipient.forEach((r) => {
      (r.issues || []).forEach((iss) => {
        flatIssues.push({
          ...iss,
          _recipient_name: r.recipient_name || 'Unknown',
          _recipient_type: (r.recipient_type || '').toString().replace(/^./, (m) => m.toUpperCase()),
        });
      });
    });
  }

  return (
    <div data-testid={`bio-${testidPrefix}`}>
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <span className="font-medium text-[var(--ink-navy)]">{label}</span>
        <Badge variant="secondary" className="text-[10px]">{by_recipient.length} recipient(s)</Badge>
        <Badge variant="secondary" className="text-[10px] bg-amber-50 text-amber-800">
          {totals.issues || 0} issue(s)
        </Badge>
      </div>
      {by_recipient.length === 0 ? (
        <div className="text-sm text-slate-500 italic py-3">{emptyMsg}</div>
      ) : isExpanded ? (
        <table className="w-full text-sm" data-testid={`bio-${testidPrefix}-table-expanded`}>
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left px-3 py-2">Recipient</th>
              <th className="text-left px-3 py-2">Type</th>
              <th className="text-left px-3 py-2">Broken Packet</th>
              <th className="text-left px-3 py-2">Memo No</th>
              <th className="text-left px-3 py-2">Issued On</th>
              <th className="text-right px-3 py-2">Carats</th>
              <th className="text-right px-3 py-2">Pieces</th>
              {canViewValues && <th className="text-right px-3 py-2">Value</th>}
            </tr>
          </thead>
          <tbody>
            {flatIssues.map((iss) => (
              <tr
                key={iss.id || iss.issue_memo_no}
                className="border-t border-[var(--divider)]"
                data-testid={`bio-${testidPrefix}-issue-${iss.id || iss.issue_memo_no}`}
              >
                <td className="px-3 py-1.5 text-xs text-slate-700 max-w-[160px] truncate" title={iss._recipient_name}>{iss._recipient_name}</td>
                <td className="px-3 py-1.5 text-xs text-slate-500">{iss._recipient_type}</td>
                <td className="px-3 py-1.5 font-mono text-xs">{iss.broken_packet_no}</td>
                <td className="px-3 py-1.5 font-mono text-[11px] text-slate-500">{iss.issue_memo_no}</td>
                <td className="px-3 py-1.5 text-[11px] text-slate-500">{(iss.issued_at || '').slice(0, 10)}</td>
                <td className="px-3 py-1.5 text-right font-mono tabular-nums">{formatCarats(iss.carats)}</td>
                <td className="px-3 py-1.5 text-right font-mono tabular-nums">{iss.pieces}</td>
                {canViewValues && <td className="px-3 py-1.5 text-right font-mono tabular-nums">{formatINR(iss.value)}</td>}
              </tr>
            ))}
            <tr className="border-t-2 border-amber-300 bg-amber-50/60 font-medium">
              <td colSpan={5} className="px-3 py-2 text-xs uppercase tracking-wider text-slate-600">Total</td>
              <td className="px-3 py-2 text-right font-mono tabular-nums">{formatCarats(totals.carats)}</td>
              <td className="px-3 py-2 text-right font-mono tabular-nums">{totals.pieces || 0}</td>
              {canViewValues && <td className="px-3 py-2 text-right font-mono tabular-nums">{formatINR(totals.value)}</td>}
            </tr>
          </tbody>
        </table>
      ) : (
        <table className="w-full text-sm" data-testid={`bio-${testidPrefix}-table-collapsed`}>
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left px-3 py-2">Recipient</th>
              <th className="text-left px-3 py-2">Type</th>
              <th className="text-right px-3 py-2">Issues</th>
              <th className="text-right px-3 py-2">Carats</th>
              <th className="text-right px-3 py-2">Pieces</th>
              {canViewValues && <th className="text-right px-3 py-2">Value</th>}
            </tr>
          </thead>
          <tbody>
            {by_recipient.map((r) => {
              const sub = r.subtotal || {};
              return (
                <tr key={`${r.recipient_type}-${r.recipient_id}`} className="border-t border-[var(--divider)]">
                  <td className="px-3 py-1.5 text-xs text-slate-700 max-w-[220px] truncate" title={r.recipient_name}>{r.recipient_name || 'Unknown'}</td>
                  <td className="px-3 py-1.5 text-xs text-slate-500 capitalize">{r.recipient_type}</td>
                  <td className="px-3 py-1.5 text-right font-mono tabular-nums text-xs">{(r.issues || []).length}</td>
                  <td className="px-3 py-1.5 text-right font-mono tabular-nums">{formatCarats(sub.carats)}</td>
                  <td className="px-3 py-1.5 text-right font-mono tabular-nums">{sub.pieces || 0}</td>
                  {canViewValues && <td className="px-3 py-1.5 text-right font-mono tabular-nums">{formatINR(sub.value)}</td>}
                </tr>
              );
            })}
            <tr className="border-t-2 border-amber-300 bg-amber-50/60 font-medium">
              <td colSpan={2} className="px-3 py-2 text-xs uppercase tracking-wider text-slate-600">Total</td>
              <td className="px-3 py-2 text-right font-mono tabular-nums">{totals.issues || 0}</td>
              <td className="px-3 py-2 text-right font-mono tabular-nums">{formatCarats(totals.carats)}</td>
              <td className="px-3 py-2 text-right font-mono tabular-nums">{totals.pieces || 0}</td>
              {canViewValues && <td className="px-3 py-2 text-right font-mono tabular-nums">{formatINR(totals.value)}</td>}
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
}

// goldsmith material ledgers when materials are issued.
function CustomerRepairItemsWipSection({ wip }) {
  const cri = wip?.customer_repair_items;
  if (!cri) return null;
  const ext = cri.external || { goldsmiths: [], totals: {} };
  const intr = cri.internal || { goldsmiths: [], totals: {} };
  const hasAny = (ext.totals?.count || 0) + (intr.totals?.count || 0) > 0;
  return (
    <Card className="overflow-hidden" data-testid="section-customer-repairs">
      <div className="px-5 py-3 bg-rose-700 text-white flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Hammer size={16} />
          <span className="font-medium">3a. Customer Repair Items in WIP</span>
          <span className="text-[11px] opacity-80 ml-2">(separate from valuation)</span>
        </div>
        <Badge className="bg-white/20 text-white border-white/30" data-testid="customer-repairs-count">
          {(ext.totals?.count || 0) + (intr.totals?.count || 0)} item(s)
        </Badge>
      </div>
      <div className="p-5 space-y-4">
        {!hasAny && (
          <div className="text-sm text-slate-500 italic py-3" data-testid="customer-repairs-empty">
            No customer or internal repair items currently with any goldsmith.
          </div>
        )}
        <RepairBucketGroup label="External (Customer-owned)" section={ext} tone="external" />
        <RepairBucketGroup label="Internal (Our jewellery)" section={intr} tone="internal" />
      </div>
    </Card>
  );
}

function RepairBucketGroup({ label, section, tone }) {
  const gs = section?.goldsmiths || [];
  const totals = section?.totals || {};
  const isExternal = tone === 'external';
  const headerCls = isExternal
    ? 'bg-rose-50 text-rose-900 border-rose-200'
    : 'bg-emerald-50 text-emerald-900 border-emerald-200';
  if (gs.length === 0) return null;
  return (
    <div className="border border-[var(--divider)] rounded-md overflow-hidden" data-testid={`customer-repairs-${tone}`}>
      <div className={`px-3 py-2 text-xs font-medium border-b ${headerCls}`}>
        <span>{label}</span>
        <span className="font-mono tabular-nums ml-2 opacity-80">
          {totals.count || 0} item(s) · {formatGrams(totals.gross_weight_g || 0)} gross
          {(totals.gold_grams || 0) > 0 && ` · ${formatGrams(totals.gold_grams)} g (Au)`}
          {(totals.diamond_carats || 0) > 0 && ` · ${formatCarats(totals.diamond_carats)} ct (Dia)`}
          {(totals.color_stone_carats || 0) > 0 && ` · ${formatCarats(totals.color_stone_carats)} ct (CS)`}
          {(totals.pearl_carats || 0) > 0 && ` · ${formatCarats(totals.pearl_carats)} ct (Pearl)`}
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead className="bg-slate-50/60">
            <tr>
              <th className="text-left px-3 py-2">Goldsmith</th>
              <th className="text-left px-3 py-2">Item · Order</th>
              <th className="text-left px-3 py-2">Mode</th>
              <th className="text-right px-3 py-2">Gross<br/><span className="text-[10px] font-normal text-slate-500">grams</span></th>
              <th className="text-right px-3 py-2">Gold<br/><span className="text-[10px] font-normal text-slate-500">g</span></th>
              <th className="text-right px-3 py-2">Diamonds<br/><span className="text-[10px] font-normal text-slate-500">ct / pcs</span></th>
              <th className="text-right px-3 py-2">CS<br/><span className="text-[10px] font-normal text-slate-500">ct / pcs</span></th>
              <th className="text-right px-3 py-2">Pearls<br/><span className="text-[10px] font-normal text-slate-500">ct / pcs</span></th>
            </tr>
          </thead>
          <tbody>
            {gs.map((g) => (
              <React.Fragment key={g.goldsmith_id}>
                {(g.items || []).map((it, idx) => (
                  <tr
                    key={it.id}
                    className="border-t border-[var(--divider)]"
                    data-testid={`customer-repair-row-${it.id}`}
                  >
                    <td className="px-3 py-2 text-xs">
                      {idx === 0 ? (
                        <Link
                          to={`/goldsmiths/${g.goldsmith_id}`}
                          className="text-[var(--ink-navy)] hover:underline"
                        >
                          {g.goldsmith_name}
                        </Link>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-[12px] text-[var(--ink-navy)] truncate max-w-xs" title={it.title}>
                        {it.title}
                      </div>
                      {it.order_no && (
                        <Link
                          to={`/orders/${it.order_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-mono text-[10px] text-[var(--jewellery-icon)] hover:underline"
                        >
                          {it.order_no}
                        </Link>
                      )}
                      {isExternal && it.customer_name && (
                        <div className="text-[10px] text-slate-500">Customer: {it.customer_name}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <Badge
                        variant="secondary"
                        className={`text-[10px] ${it.detail_mode === 'gross' ? 'bg-amber-50 text-amber-800' : 'bg-emerald-50 text-emerald-800'}`}
                      >
                        {it.detail_mode === 'gross' ? 'Gross only' : 'Detailed'}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-xs">
                      {formatGrams(it.incoming_gross_weight_g || 0)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-xs">
                      {formatGrams(it.gold_grams || 0)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-xs">
                      {formatCarats(it.diamond_carats || 0)} / {it.diamond_pieces || 0}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-xs">
                      {formatCarats(it.color_stone_carats || 0)} / {it.color_stone_pieces || 0}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-xs">
                      {formatCarats(it.pearl_carats || 0)} / {it.pearl_pieces || 0}
                    </td>
                  </tr>
                ))}
                {(g.items || []).length > 0 && (
                  <tr className={`border-t border-[var(--divider)] bg-slate-50/60`}>
                    <td colSpan={3} className="px-3 py-1.5 text-[11px] text-slate-600 italic">
                      Subtotal — {g.goldsmith_name}
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono tabular-nums text-[11px]">
                      {formatGrams(g.subtotal?.gross_weight_g || 0)}
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono tabular-nums text-[11px]">
                      {formatGrams(g.subtotal?.gold_grams || 0)}
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono tabular-nums text-[11px]">
                      {formatCarats(g.subtotal?.diamond_carats || 0)} / {g.subtotal?.diamond_pieces || 0}
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono tabular-nums text-[11px]">
                      {formatCarats(g.subtotal?.color_stone_carats || 0)} / {g.subtotal?.color_stone_pieces || 0}
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono tabular-nums text-[11px]">
                      {formatCarats(g.subtotal?.pearl_carats || 0)} / {g.subtotal?.pearl_pieces || 0}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
